import { jsx, jsxs } from "react/jsx-runtime";
import {
  SkeletonItem,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableRow,
  __css,
  mergeClasses,
  tokens
} from "@fluentui/react-components";
import {
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable
} from "@tanstack/react-table";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { box } from "../../shell/styles.js";
import { layout, typeScale } from "../../theme/tokens.js";
import { useVisuallyHiddenStyles } from "../internal/styles.js";
import { useOverflowX } from "../internal/use-overflow-x.js";
const DEFAULT_COLUMN_MIN_WIDTH = 120;
const useStyles = __css({"root":{"qhf8xq":"f10pi13n","B68tc82":"f1oy3dpc","Bmxbyg5":"f1sil6mw","B4m11q7":"f32hy0g","rm1204":"fwyddsq","ijz38e":"f1ftuoky","tzxy30":"fgmlvap","Bwezv66":"fwl1lcr","Ctt3hk":"f1crbv9w","gggo0z":"fi15kbk","B834pou":"f1taw3pw","F1poyb":"foi3q75","Behc1zw":"f10zy6vx","uk9mye":"f1p2dij1","mbw9mh":"f1a566p","Beyfa6y":0,"Bbmb7ep":0,"Btl43ni":0,"B7oj6ja":0,"Dimara":"f1o0qvyv","B7salou":"f1pbl6dw","Beohmeb":"f1u5sdlw","B486eqv":"f1u8i3ug","vu0emg":"fvi4nxw","Byl69ji":"fz7cnr3"},"caption":{"fsow6f":"fpgzoln","Dbcxam":0,"rjzwhg":0,"Bblux5w":"f9b8bpc","sj55zd":"fkfq4zb","Bhrd7zp":"fl43uef"},"comfortable":{"sshi5w":"f5pgtk9","mbw9mh":"f1a566p","Jwef8y":"f1knas48"},"compact":{"sshi5w":"fv9hx5d","mbw9mh":"f1a566p","Jwef8y":"f1knas48"},"headerRow":{"De3pzq":"f1dmdbja","Jwef8y":"f3q7cyz"},"headerCell":{"Be2twd7":"f1ugzwwg","Bg96gwp":"f104wqfl","Bhrd7zp":"fdj6btp","sj55zd":"f11d4kpn","j79ot6":"f1vyl8qn","oq9wiu":"fvo76of","ncli4a":"fe5fs37","Bqlqnjs":"f1lguhbf"},"clickable":{"Bceei9c":"f1k6fduh","Beohmeb":"f1u5sdlw","B486eqv":"f1u8i3ug","vu0emg":"fvi4nxw","Byl69ji":"fz7cnr3"},"numericCell":{"fsow6f":"f12ymhq5","B7salou":"f1pbl6dw","Brf1p80":"f9c4gz4"},"numericHeaderButton":{"Brf1p80":"f9c4gz4"},"empty":{"Dbcxam":0,"rjzwhg":0,"Bblux5w":"f1t52415"}});
function DataTable({
  columns,
  data,
  getRowId,
  sorting,
  onSortingChange,
  defaultSorting = [],
  manualSorting = false,
  onRowClick,
  loading = false,
  loadingRowCount = 5,
  empty,
  caption,
  captionVisible = false,
  density = "comfortable",
  className
}) {
  const { t } = useTranslation("ui");
  const styles = useStyles();
  const [scrollerRef, overflows] = useOverflowX();
  const hidden = useVisuallyHiddenStyles();
  const [internalSorting, setInternalSorting] = useState(defaultSorting);
  const state = sorting ?? internalSorting;
  const handleSortingChange = (updater) => {
    const next = typeof updater === "function" ? updater(state) : updater;
    if (sorting === void 0) setInternalSorting(next);
    onSortingChange?.(next);
  };
  const table = useReactTable({
    data,
    columns,
    getRowId,
    state: { sorting: state },
    onSortingChange: handleSortingChange,
    manualSorting,
    enableSortingRemoval: true,
    getCoreRowModel: getCoreRowModel(),
    ...manualSorting ? {} : { getSortedRowModel: getSortedRowModel() }
  });
  const size = density === "compact" ? "small" : "medium";
  const rowClass = density === "compact" ? styles.compact : styles.comfortable;
  const rows = table.getRowModel().rows;
  const leafColumns = table.getVisibleLeafColumns();
  const showEmpty = !loading && rows.length === 0 && empty;
  const minWidthOf = (meta) => meta?.minWidth ?? DEFAULT_COLUMN_MIN_WIDTH;
  const tableMinWidth = leafColumns.reduce((sum, c) => sum + minWidthOf(c.columnDef.meta), 0);
  const onRowKeyDown = (event, row) => {
    if (event.target !== event.currentTarget) return;
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      onRowClick?.(row);
    }
  };
  return /* @__PURE__ */ jsxs(
    "div",
    {
      ref: scrollerRef,
      className: mergeClasses(styles.root, className),
      ...overflows ? { role: "region", tabIndex: 0, ...caption ? { "aria-label": caption } : {} } : {},
      children: [
        /* @__PURE__ */ jsx("span", { role: "status", className: hidden.hidden, children: loading ? t("components.common.loading") : "" }),
        /* @__PURE__ */ jsxs(
          Table,
          {
            size,
            "aria-busy": loading || void 0,
            style: { minInlineSize: `${String(tableMinWidth)}px` },
            children: [
              caption ? /* @__PURE__ */ jsx("caption", { className: captionVisible ? styles.caption : hidden.hidden, children: caption }) : null,
              /* @__PURE__ */ jsx("colgroup", { children: leafColumns.map((column) => /* @__PURE__ */ jsx(
                "col",
                {
                  style: { inlineSize: `${String(minWidthOf(column.columnDef.meta))}px` }
                },
                column.id
              )) }),
              /* @__PURE__ */ jsx(TableHeader, { children: table.getHeaderGroups().map((headerGroup) => /* @__PURE__ */ jsx(TableRow, { className: mergeClasses(rowClass, styles.headerRow), children: headerGroup.headers.map((header) => {
                const column = header.column;
                const sortable = column.getCanSort();
                const direction = column.getIsSorted();
                return /* @__PURE__ */ jsx(
                  TableHeaderCell,
                  {
                    sortable,
                    className: styles.headerCell,
                    style: { inlineSize: `${String(minWidthOf(column.columnDef.meta))}px` },
                    ...direction ? {
                      sortDirection: direction === "asc" ? "ascending" : "descending"
                    } : {},
                    ...sortable ? { onClick: column.getToggleSortingHandler() } : {},
                    ...column.columnDef.meta?.numeric ? { button: { className: styles.numericHeaderButton } } : {},
                    children: header.isPlaceholder ? null : flexRender(column.columnDef.header, header.getContext())
                  },
                  header.id
                );
              }) }, headerGroup.id)) }),
              /* @__PURE__ */ jsx(TableBody, { children: loading ? Array.from({ length: loadingRowCount }, (_, index) => /* @__PURE__ */ jsx(TableRow, { className: rowClass, "aria-hidden": "true", children: leafColumns.map((column) => /* @__PURE__ */ jsx(TableCell, { children: /* @__PURE__ */ jsx(Skeleton, { "aria-hidden": "true", role: "presentation", children: /* @__PURE__ */ jsx(SkeletonItem, { size: 12 }) }) }, column.id)) }, `loading-${String(index)}`)) : rows.map((row) => /* @__PURE__ */ jsx(
                TableRow,
                {
                  className: mergeClasses(rowClass, onRowClick && styles.clickable),
                  ...onRowClick ? {
                    tabIndex: 0,
                    onClick: () => {
                      onRowClick(row.original);
                    },
                    onKeyDown: (event) => {
                      onRowKeyDown(event, row.original);
                    }
                  } : {},
                  children: row.getVisibleCells().map((cell) => /* @__PURE__ */ jsx(
                    TableCell,
                    {
                      className: mergeClasses(
                        cell.column.columnDef.meta?.numeric && styles.numericCell
                      ),
                      children: flexRender(cell.column.columnDef.cell, cell.getContext())
                    },
                    cell.id
                  ))
                },
                row.id
              )) })
            ]
          }
        ),
        showEmpty ? /* @__PURE__ */ jsx("div", { className: styles.empty, children: empty }) : null
      ]
    }
  );
}
export {
  DEFAULT_COLUMN_MIN_WIDTH,
  DataTable
};
//# sourceMappingURL=data-table.js.map

import "/opt/batiflow/app/packages/ui/dist/components/data-table/data-table.griffel.css";